<template>
  <tr class="border-t border-black/20 transition duration-300">
    <td class="align-top flex items-center gap-2">
      <div class="p-2 text-slate-700 text-sm">{{ searchNumber }}</div>

      <div
        v-if="!isSearchTimeRangeValid"
        v-tooltip="getInvalidTimeRangeWarningMessage()"
        class="text-yellow-600 relative"
      >
        <div
          class="absolute animate-ping bg-yellow-600 size-full rounded-full top-0 left-0 opacity-40"
        />
        <ExclamationIcon class="size-5" />
      </div>
    </td>

    <td class="align-top">
      <div class="p-2 text-slate-700 font-[400] text-sm">
        {{
          search.searchDate
            ? format(new Date(search.searchDate), settings.dateFormat.value)
            : '-'
        }}
      </div>
    </td>
    <td
      v-if="importSource.id === BuiltInImportSourceId.CITATION_SEARCH"
      class="align-top"
    >
      <div class="p-1 text-slate-700">
        <span
          v-tooltip="
            'Title: ' +
            (getStudyById(search.parentStudyId!) as Article)?.metadata?.title
          "
          class="flex items-center gap-1 w-fit"
        >
          {{ search.parentStudyId ?? '-' }}
          <InformationCircleIcon class="text-black-20 w-4 h-4" />
        </span>
      </div>
    </td>
    <td
      v-if="
        importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
        importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
      "
      class="align-top text-center p-2"
    >
      <button @click="toggleRowExpansion">
        <ChevronDownFilledIcon v-if="isRowExpanded" />
        <ChevronRightFilledIcon v-else />
      </button>
    </td>
    <td
      v-if="
        importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
        importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
      "
      class="align-top max-w-[400px]"
    >
      <ExpansionPanel
        v-model="isRowExpanded"
        class="p-2 text-slate-700 text-sm"
      >
        <template #activator>
          <div class="space-y-1">
            <p :class="{ 'line-clamp-1': !isRowExpanded }">
              <span class="font-medium">Search: </span>
              <span class="font-[400]">{{
                search.searchQuery ? search.searchQuery : '-'
              }}</span>
            </p>
            <p :class="{ 'line-clamp-1': !isRowExpanded }">
              <span class="font-medium">Filters: </span>
              {{
                search.filters
                  ? importSource.id === BuiltInImportSourceId.PMC
                    ? search.filters
                    : formatDatesInSearchFilters(search.filters)
                  : '-'
              }}
            </p>
          </div>
        </template>
        <template #default>
          <div class="space-y-2 mt-2">
            <div
              v-if="
                importSource.id === BuiltInImportSourceId.PUBMED ||
                importSource.id === BuiltInImportSourceId.PMC
              "
              class="align-top"
            >
              <div class="text-slate-700">
                <span class="font-semibold">Search details:</span>
                {{
                  search.queryTranslation
                    ? updateEndDateInPubmedQuery(search.queryTranslation)
                    : '-'
                }}
              </div>
            </div>
          </div>
        </template>
      </ExpansionPanel>
    </td>

    <td class="align-top"></td>
    <td class="align-top text-center">
      <div class="p-2 text-slate-700 text-sm">
        <a
          v-if="getSearchUrl(search)"
          :href="getSearchUrl(search)"
          target="_blank"
          rel="noopener"
          class="underline text-primary"
        >
          {{ search.studies?.length }}
        </a>
        <div v-else>{{ search.studies?.length }}</div>
      </div>
    </td>
    <td class="align-top">
      <div class="pt-2 flex justify-end">
        <PlanLockGuard>
          <FloatingMenu>
            <template #activator="{ show }">
              <button
                class="flex px-2 justify-center items-center rounded-full text-red-600"
                :disabled="review.isPlanReadonly.value"
                @click="show"
              >
                <TrashIcon class="w-4 h-4" />
              </button>
            </template>
            <template #content="{ hide }">
              <div class="p-4 space-y-2">
                <div>
                  <p v-if="doesSearchContainScreenedStudies(search)">
                    This search contains screened studies, are you sure you want
                    to delete it?
                  </p>
                  <p v-else>Are you sure you want to delete this search?</p>
                </div>
                <div>
                  <button
                    class="bg-neutral-500 hover:bg-neutral-600 text-white py-1 px-2 mr-1"
                    @click.stop="hide"
                  >
                    Cancel
                  </button>
                  <button
                    class="bg-red-500 hover:bg-red-600 text-white py-1 px-2"
                    @click.stop="
                      () => {
                        removeSearch(search).then(() => hide())
                      }
                    "
                  >
                    Delete
                  </button>
                </div>
              </div>
            </template>
          </FloatingMenu>
        </PlanLockGuard>
      </div>
    </td>
  </tr>
</template>
<script setup lang="ts">
import { Search } from '@core/domain/models/search.model'
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import { updateEndDateInPubmedQuery } from '@app/utils/updateEndDateInPubmedQuery'
import { Article } from '@core/domain/models/article.model'
import ExpansionPanel from '@app/components/Global/ExpansionPanel.vue'
import { format, parse, isValid } from 'date-fns'
import { useSettings } from '@app/composables/use-settings'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { HttpException } from '@core/exceptions/http.exception'
import { errorMessage } from '@app/utils/error-message'
import { ReviewKey } from '@app/views/Review/use-review'
import { injectStrict } from '@app/utils/injectStrict'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import { ImportSource } from '@core/domain/models/import-source.model'
import TrashIcon from '@app/components/Icons/TrashIcon.vue'
import PlanLockGuard from '@app/views/Review/Plan/PlanLockGuard.vue'
import { computed, ref } from 'vue'
import ChevronDownFilledIcon from '@app/components/Icons/ChevronDownFilledIcon.vue'
import ChevronRightFilledIcon from '@app/components/Icons/ChevronRightFilledIcon.vue'
import ExclamationIcon from '@app/components/Icons/ExclamationIcon.vue'
import {
  extractSearchDates,
  normalizeDate,
  parseDate,
} from '@app/utils/extractSearchDates'

const props = defineProps<{
  search: Search
  importSource: ImportSource
  searchNumber: number
}>()

const loading = useLoading()
const snackbar = useSnackbar()

function toggleRowExpansion() {
  isRowExpanded.value = !isRowExpanded.value
}

const review = injectStrict(ReviewKey)

const isRowExpanded = ref(false)

const settings = useSettings()

function getSearchUrl(search: Search) {
  return search.getSearchUrl()
}

function formatDatesInSearchFilters(searchFilters: string): string {
  const dateMatches = searchFilters.match(/\d{1,4}([/.-])\d{1,2}\1\d{2,4}/g)

  if (dateMatches) {
    const formattedDates = dateMatches.map((dateStr) => {
      let parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date())

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, 'yyyy/MM/dd', new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, 'dd-MM-yyyy', new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, 'dd.MM.yyyy', new Date())
      }

      if (!isValid(parsedDate)) {
        parsedDate = parse(dateStr, 'yyyy.MM.dd', new Date())
      }

      if (isValid(parsedDate)) {
        return {
          original: dateStr,
          formatted: format(parsedDate, settings.dateFormat.value),
        }
      } else {
        return {
          original: dateStr,
          formatted: dateStr,
        }
      }
    })

    return formattedDates.reduce(
      (newText, { original, formatted }) =>
        newText.replace(original, formatted),
      searchFilters,
    )
  }

  return searchFilters
}

async function removeSearch(search: Search) {
  if (!search) throw new Error('no search selected')
  try {
    loading.start()
    await review.removeSearch(search.id!)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
  } finally {
    loading.stop()
  }
}

function doesSearchContainScreenedStudies(search: Search) {
  return search.studies?.some(
    (s) => s.titleAndAbstractScreening ?? s.fullTextScreening,
  )
}

function getStudyById(id: number) {
  const study = review.entity.value.studies?.find((s) => s.id === id)
  return study
}

const isSearchTimeRangeValid = computed(() => {
  const studyDesignTimeLimitFromDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitFromDate
  const studyDesignTimeLimitToDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitToDate

  const dateRange = extractSearchDates(
    props.search.searchQuery ?? null,
    props.search.filters ?? null,
  )

  if (dateRange && dateRange.startDate && dateRange.endDate) {
    const parsedStartDate = parseDate(dateRange.startDate)
    const parsedEndDate = parseDate(dateRange.endDate)

    if (parsedStartDate && parsedEndDate) {
      if (studyDesignTimeLimitFromDate) {
        const limitFromDate = new Date(studyDesignTimeLimitFromDate)
        if (normalizeDate(parsedStartDate) < normalizeDate(limitFromDate)) {
          return false
        }
      }
      if (studyDesignTimeLimitToDate) {
        const limitToDate = new Date(studyDesignTimeLimitToDate)
        if (normalizeDate(parsedEndDate) > normalizeDate(limitToDate)) {
          return false
        }
      }
    }
  }
  return true
})

function getInvalidTimeRangeWarningMessage(): string {
  const studyDesignTimeLimitFromDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitFromDate
  const studyDesignTimeLimitToDate =
    review.entity.value.plan?.inclusionCriteria.timeLimitToDate

  if (studyDesignTimeLimitFromDate && studyDesignTimeLimitToDate)
    return (
      'Search time range does not match plan strategy time limits: ' +
      format(
        new Date(studyDesignTimeLimitFromDate),
        settings.dateFormat.value,
      ) +
      ' - ' +
      format(new Date(studyDesignTimeLimitToDate), settings.dateFormat.value)
    )
  return ''
}
</script>
